import * as React from "react";
import Icon from "@mui/material/Icon";
import type { SxProps, Theme } from "@mui/material/styles";
import { isMuiIconName } from "../../constants/muiIcons"; // Set-based guard

type SafeIconProps = {
  /** name of the component from @mui/icons-material, e.g. "HomeOutlined" */
  name: string | null | undefined;

  /** can disable the check by the list, if name is always "clean" */
  validateName?: boolean;

  fallback?: React.ReactNode;
  fontSize?: "inherit" | "small" | "medium" | "large";
  sx?: SxProps<Theme>;
  className?: string;
  titleAccess?: string;
};

const NUMBER_WORDS: Array<[string, string]> = [
  ["Zero", "0"],
  ["One", "1"],
  ["Two", "2"],
  ["Three", "3"],
  ["Four", "4"],
  ["Five", "5"],
  ["Six", "6"],
  ["Seven", "7"],
  ["Eight", "8"],
  ["Nine", "9"],
  ["Ten", "10"],
];

const DIGIT_WORD_EXCEPTIONS = new Set([
  "3d",
  "4k",
  "5g",
  "1x",
]);

function pascalToSnake(s: string) {
  return s
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .replace(/([a-zA-Z])([0-9])/g, "$1_$2")
    .replace(/([0-9])([a-zA-Z])/g, "$1_$2")
    .toLowerCase();
}

function replaceNumberWords(snake: string) {
  let s = snake;

  for (const [word, digit] of NUMBER_WORDS) {
    const w = word.toLowerCase();

    // three_d → 3_d
    s = s.replace(
      new RegExp(`(^|_)${w}(_|$)`, "g"),
      `$1${digit}$2`
    );

    // three_d_rotation → 3_d_rotation
    s = s.replace(
      new RegExp(`(^|_)${w}(_[a-z])`, "g"),
      `$1${digit}$2`
    );
  }

  return s;
}

function applyDigitWordExceptions(snake: string) {
  let s = snake;

  for (const ex of DIGIT_WORD_EXCEPTIONS) {
    // _3_d_ → _3d_
    s = s.replace(
      new RegExp(`(^|_)${ex[0]}_${ex[1]}(?=_|$)`, "g"),
      `$1${ex}`
    );
  }

  return s;
}

/** Mini-override for real encountered exceptions (add as needed) */
const LIGATURE_OVERRIDES: Record<string, string> = {
  // example:
  // "SomethingWeird": "actual_ligature"
  "Co2": "co2",
  "EightK": "8k",
  "EightMp": "8mp",
  "EightKPlus": "8k_plus",
  "EightUpRating": "8up_rating",
  "EighteenMp": "18mp",
  "EightteenMp": "18mp",
  "EighteenUpRating": "18_up_rating",
  "ElevenMp": "11mp",
  "FifteenMp": "15mp",
  "FiveK": "5k",
  "FiveKPlus": "5k_plus",
  "FiveMp": "5mp",
  "FiveUpRating": "5up_rating",
  "FivteenMp": "15mp",
  "FourGMobiledata": "4g_mobiledata",
  "FourGPlusMobiledata": "4g_plus_mobiledata",
  "FourMp": "4mp",
  "FourUpRating": "4up_rating",
  "FourteenMp": "14mp",
  "GitHub": "github",
  "Grid3x3": "grid_3x3",
  "Grid4x4": "grid_4x4",
  "NineK": "9k",
  "NineKPlus": "9k_plus",
  "NineMp": "9mp",
  "NineteenMp": "19mp",
  "OneK": "1k",
  "OneKk": "1kk",
  "OneKPlus": "1k_plus",
  "PlusOne": "plus_one",
  "SevenK": "7k",
  "SevenKPlus": "7k_plus",
  "SevenMp": "7mp",
  "SeventeenMp": "17mp",
  "SixK": "6k",
  "SixKPlus": "6k_plus",
  "SixMp": "6mp",
  "SixteenMp": "16mp",
  "SixtyFps": "60fps",
  "SixtyFpsSelect": "60fps_select",
  "TenMp": "10mp",
  "ThirteenMp": "13mp",
  "ThirtyFps": "30fps",
  "ThirtyFpsSelect": "30fps_select",
  "ThreeGMobiledata": "3g_mobiledata",
  "ThreeK": "3k",
  "ThreeKPlus": "3k_plus",
  "ThreeMp": "3mp",
  "ThreeP": "3p",
  "ThreeSixty": "360",
  "TwelveMp": "12mp",
  "TwentyFourMp": "24mp",
  "TwentyOneMp": "21mp",
  "TwentyTwoMp": "22mp",
  "TwentyThreeMp": "23mp",
  "TwentyZeroMp": "20mp",
  "TwoK": "2k",
  "TwoKPlus": "2k_plus",
  "TwoMp": "2mp",
  "TwoWheeler": "two_wheeler",
};

function toLigature(muiComponentName: string) {
  const base = muiComponentName;

  const override = LIGATURE_OVERRIDES[base];
  if (override) return override;

  let s = pascalToSnake(base);
  s = replaceNumberWords(s);
  s = applyDigitWordExceptions(s);
  return s;
}

// example: <SafeIcon name={value} sx={{ color: '#666' }} fontSize="large" fallback={<ImageIcon sx={{ fontSize: 40, color: '#666' }} />} />
export function SafeIcon({
  name,
  validateName = true,
  fallback = null,
  fontSize = "medium",
  sx,
}: SafeIconProps) {
  if (!name) return <>{fallback}</>;

  if (validateName && !isMuiIconName(name)) return <>{fallback}</>;

  const ligature = toLigature(name);

  return (
    <Icon
      fontSize={fontSize}
      sx={sx}
    >
      {ligature}
    </Icon>
  );
}
